'use client';

import { useIsClient } from 'usehooks-ts';
import { useGeoSearchString } from '@/contexts/CookieContext';
import React, { Fragment, useEffect, useMemo, useRef } from 'react';
import { buildGeoPrefixedPath } from '../../main-page/CategoriesShortcuts/GeoAwareCategoryLink.client';
import { getLink } from '@/utils/serverSetCategoryLink';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';

type Breadcrumb = { url: string; name: string };

const normalizePath = (value?: string | null) => (value || '').replace(/^\/+|\/+$/g, '');

function GeoAwareBreadcrumbsInternal({
  breadcrumbs,
  hideLastElem = true,
  initialSearchLink = '',
}: {
  breadcrumbs: Breadcrumb[];
  hideLastElem?: boolean;
  initialSearchLink?: string;
}) {
  const geoSearchString = useGeoSearchString();
  const mounted = useIsClient();
  const listRef = useRef<HTMLOListElement>(null);

  const normalizedGeo = mounted ? normalizePath(geoSearchString) : '';

  const geoFixedBreadcrumbs = useMemo(() => {
    return breadcrumbs.map((bc) => ({
      name: bc.name,
      resUrl: mounted ? buildGeoPrefixedPath(normalizedGeo, bc.url) : getLink(`/${bc.url}`, initialSearchLink),
    }));
  }, [normalizedGeo, breadcrumbs, mounted, initialSearchLink]);

  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const isRtl = getComputedStyle(list).direction === 'rtl';
    list.scrollLeft = isRtl ? -list.scrollWidth : list.scrollWidth;
  }, [geoFixedBreadcrumbs]);

  const lastIndex = geoFixedBreadcrumbs.length - 1;

  return (
    <Breadcrumb suppressHydrationWarning className='w-full min-w-0'>
      <BreadcrumbList
        ref={listRef}
        className='hide-scroll flex-nowrap gap-1 overflow-x-auto text-[12px] leading-5 text-text-secondary md:flex-wrap md:gap-1.5 md:overflow-visible md:text-[14px] sm:gap-1.5'
      >
        {geoFixedBreadcrumbs.map(({ resUrl, name }, index) => {
          const isLast = index === lastIndex;
          return (
            <Fragment key={`${resUrl}-${index}`}>
              <BreadcrumbItem className='shrink-0 whitespace-nowrap'>
                {isLast && hideLastElem ? (
                  <BreadcrumbPage className='font-medium text-text-primary'>{name}</BreadcrumbPage>
                ) : (
                  <BreadcrumbLink
                    href={resUrl}
                    data-test-test={resUrl}
                    className='rounded-[4px] text-text-secondary outline-offset-2 hover:text-text-primary focus-visible:outline-2 focus-visible:outline-main-green'
                  >
                    {name}
                  </BreadcrumbLink>
                )}
              </BreadcrumbItem>
              {!isLast && <BreadcrumbSeparator className='shrink-0 text-icon-default [&>svg]:size-3.5' />}
            </Fragment>
          );
        })}
      </BreadcrumbList>
    </Breadcrumb>
  );
}

export const GeoAwareBreadcrumbs = React.memo(GeoAwareBreadcrumbsInternal);
